import { Navigate } from 'react-router-dom'
import { type JSX } from 'react'
import { useMedplum } from '@medplum/react'
import { usePractitionerOpsScope } from '@/hooks/usePractitionerOpsScope'

type ProtectedRouteProps = {
  children: JSX.Element | string
  level?: 'user' | 'system' | 'call-center-agent' | 'practitioner-ops'
  isCallCenterManager?: boolean
}

export const ProtectedRoute = ({
  children,
  level = 'user',
  isCallCenterManager = false,
}: ProtectedRouteProps) => {
  const medplum = useMedplum()
  // Only the practitioner-ops level asks the middleware; the other levels never fire the query.
  const practitionerOps = usePractitionerOpsScope({ enabled: level === 'practitioner-ops' })

  if (!medplum.isAuthenticated()) {
    return <Navigate to="/login" replace />
  }

  if (level === 'system' && !medplum.isSuperAdmin()) {
    if (medplum.isLoading()) return
    return <Navigate to="/" replace />
  }

  if (isCallCenterManager && level !== 'call-center-agent') {
    if (medplum.isLoading()) return
    return <Navigate to="/" replace />
  }

  // Provider Operations admits exactly the callers whose scope is non-empty (Care
  // Coordinators, super admins); anyone else typing the URL is sent home, like the
  // call-center redirect above.
  if (level === 'practitioner-ops') {
    if (practitionerOps.isLoading) return null
    if (practitionerOps.organizations.length === 0) return <Navigate to="/" replace />
  }

  return children
}
